import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import { useNavigate, useLocation } from 'react-router-dom'
import { ESTADO_PUBLICADA } from '../../../constants/publicaciones'
import { toastErrorLog } from '../../../helpers/toastErrorLog'
import { useAuth } from '../../../hooks/useAuth'
import { useImagenesPublicacion } from '../../../hooks/useImagenesPublicacion.js'
import { usePaginacion } from '../../hooks/usePaginacion'
import ComponenteError from '../../publicaciones/ComponenteError'
import { Paginacion } from '../../reutilizables/paginacion/Paginacion'
import * as mensajesService from '../../servicios/mensajesService'
import { buscarTodasOptimizado } from '../../servicios/publicacionesService'
import DetallePreguntas from './DetallePreguntas'
import styles from './PreguntasPublicaciones.module.css'
import Publicacion from './Publicacion'
import pubStyles from './Publicacion.module.css'

const CANTIDAD_SKELETONS = 4
const ITEMS_POR_PAGINA = 15

const PreguntasPublicaciones = ({ tipo }) => {
  const { loginDataContext: { idUsuario, empid } = {}, esPerfilAdmin } = useAuth()
  const navigate = useNavigate()

  const [titulo, setTitulo] = useState('')
  const [items, setItems] = useState([])
  const [sinResultados, setSinResultados] = useState(false)
  const [datosCargados, setDatosCargados] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [idsModal, setIdsModals] = useState({})

  const { adjuntos, loadingImagenes, fetchImagenes } = useImagenesPublicacion()
  const { state: navState } = useLocation()

  const fetchData = async () => {
    if (!idUsuario) {
      setDatosCargados(true)
      setSinResultados(true)
      return
    }
    let filtradas = []
    try {
      if (tipo === 'comprador') {
        const { data: vendedores } = await mensajesService.buscarEnPublicaciones(idUsuario, '')
        filtradas = vendedores
          .flatMap((v) => v.publicaciones ?? [])
          .filter((pub) => pub.idVendedor !== idUsuario)
      } else {
        const empidAdmin = esPerfilAdmin ? empid : null
        const { data: publicaciones } = await buscarTodasOptimizado(0, idUsuario, 0, '', empidAdmin)
        filtradas = publicaciones.filter((pub) => pub.tieneMensaje)
      }
    } catch (error) {
      toastErrorLog(error, 'No se pudieron cargar las preguntas.', 'preguntas')
    }
    setItems(filtradas)
    setSinResultados(filtradas.length === 0)
    setDatosCargados(true)
  }
  useEffect(() => {
    fetchData()
    setTitulo(tipo === 'comprador' ? 'Preguntas Realizadas' : 'Preguntas Recibidas')
  }, [idUsuario, tipo])

  const {
    itemsActual,
    paginaActual,
    setPaginaActual,
    itemsPorPagina,
    setItemsPorPagina,
    totalPaginas
  } = usePaginacion(items, ITEMS_POR_PAGINA)

  useEffect(() => {
    if (itemsActual.length > 0) {
      fetchImagenes({
        idPublicaciones: itemsActual.map((item) => item.idPublicacion)
      })
    }
  }, [itemsActual])

  const cambiarPagina = (pagina) => {
    setPaginaActual(pagina)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const onClick = (idPublicacion, idVendedor, idCompradorFiltro = null) => {
    setShowModal(true)
    setIdsModals({ idPublicacion, idVendedor, idCompradorFiltro })
  }

  useEffect(() => {
    if (datosCargados && navState?.idPublicacion && navState?.idVendedor) {
      const publicacionExiste = items.some((p) => p.idPublicacion === navState.idPublicacion)
      if (!publicacionExiste) {
        toastErrorLog(null, 'No se encontró la publicación.', 'publicaciones')
        navigate(tipo === 'comprador' ? '/PreguntasComprador' : '/PreguntasVendedor', {
          replace: true,
          state: null
        })
        return
      }
      onClick(navState.idPublicacion, navState.idVendedor, navState.idComprador ?? null)
      navigate('.', { replace: true, state: null })
    }
  }, [navState, items, datosCargados])
  const handleClose = () => {
    setShowModal(false)
    setIdsModals({})
  }

  const onIrAPublicacionClick = () => {
    navigate(`/publicacion/${idsModal.idPublicacion}`)
  }

  const esComprador = tipo === 'comprador'
  const publicacionModal = items.find((p) => p.idPublicacion === idsModal.idPublicacion)
  const cantidad = items.length

  return (
    <>
      <div className={styles.container}>
        <header className={styles.cabecera}>
          <span className={styles.cabeceraIcono} aria-hidden="true">
            <i className={`fa ${esComprador ? 'fa-question-circle-o' : 'fa-comments-o'}`} />
          </span>
          <div className={styles.cabeceraTextos}>
            <h2 className={styles.titulo}>{titulo}</h2>
            <p className={styles.subtitulo}>
              {esComprador
                ? 'Publicaciones en las que hiciste preguntas.'
                : 'Publicaciones que recibieron preguntas de compradores.'}
            </p>
          </div>
          {datosCargados && !sinResultados && (
            <span className={styles.contador}>
              {cantidad} {cantidad === 1 ? 'publicación' : 'publicaciones'}
            </span>
          )}
        </header>

        {sinResultados ? (
          <div className="row">
            <ComponenteError tipo="I" idAviso={7} />
          </div>
        ) : !datosCargados ? (
          <div className={pubStyles.lista} aria-busy="true" aria-label="Cargando publicaciones">
            {Array.from({ length: CANTIDAD_SKELETONS }, (_, i) => (
              <div key={i} className={styles.skeleton}>
                <span className={styles.skeletonImagen} />
                <span className={styles.skeletonLineas}>
                  <span className={styles.skeletonLinea} />
                  <span className={`${styles.skeletonLinea} ${styles.skeletonLineaCorta}`} />
                </span>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className={pubStyles.lista}>
              {itemsActual.map((publicacion) => (
                <Publicacion
                  key={publicacion.idPublicacion}
                  onClick={onClick}
                  publicacion={publicacion}
                  adjunto={adjuntos.find((a) => a.idPublicacion === publicacion.idPublicacion)}
                  loadingImagenes={loadingImagenes}
                />
              ))}
            </div>
            {items.length > ITEMS_POR_PAGINA && (
              <Paginacion
                totalPaginas={totalPaginas}
                paginaActual={paginaActual}
                onCambioPagina={cambiarPagina}
                itemsPorPagina={itemsPorPagina}
                onCambioItemsPorPagina={(cantidad) => {
                  setItemsPorPagina(cantidad)
                  cambiarPagina(1)
                }}
              />
            )}
          </>
        )}
      </div>

      <Modal
        show={showModal}
        onHide={handleClose}
        size={'lg'}
        animation={false}
        scrollable
        dialogClassName={styles.modalDialog}
        contentClassName={styles.modalContent}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title as="div" className={styles.modalTitulo}>
            <span className={styles.modalEtiqueta}>Preguntas</span>
            <h3 className={styles.modalNombre} title={publicacionModal?.titulo}>
              {publicacionModal?.titulo}
            </h3>
            <div className={styles.modalAcciones}>
              <button
                type="button"
                className={styles.irPublicacion}
                onClick={onIrAPublicacionClick}
              >
                Ir a la publicación
                <i className="fa fa-external-link" aria-hidden="true" />
              </button>
              {publicacionModal?.idPublicacionEstado !== ESTADO_PUBLICADA && (
                <span className={styles.noVigente}>
                  <i className="fa fa-exclamation-triangle" aria-hidden="true" />
                  Esta publicación no está vigente
                </span>
              )}
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <DetallePreguntas
            idPublicacion={idsModal.idPublicacion}
            idPublicacionEstado={publicacionModal?.idPublicacionEstado}
            vendedorNombre={publicacionModal?.vendedorNombre}
            idVendedor={idsModal.idVendedor}
            idCompradorFiltro={idsModal.idCompradorFiltro ?? null}
            tipo={tipo}
          />
        </Modal.Body>
      </Modal>
    </>
  )
}

PreguntasPublicaciones.propTypes = {
  tipo: PropTypes.oneOf(['comprador', 'vendedor']).isRequired
}

export default PreguntasPublicaciones
